import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AdminService } from '../../admin/services/admin/admin.service';
import { environment } from 'src/environments/environment';

export interface Administrator {
  id: number;
  user: {
    id: number;
    first_name: string;
    first_name2?: string;
    last_name: string;
    last_name2?: string;
    email: string;
    role: string;
    full_name: string;
    cell?: string;
    dni?: string;
  };
  alliance: {
    id: number;
    name: string;
    laboratory: {
      id: number;
      name: string;
    };
  };
  permission_level: number;
  is_active: boolean;
  date_created: string;
  date_updated?: string;
  created_by?: string;
}

export interface CreateAdministratorRequest {
  first_name: string;
  first_name2?: string;
  last_name: string;
  last_name2?: string;
  email: string;
  cell: string;
  dni: string;
  city_id: string;
  role: string;
  alliance_id: number;
  permission_level: number;
}

@Component({
  selector: 'app-admin-asociados',
  templateUrl: './admin-asociados.component.html',
  styleUrls: ['./admin-asociados.component.css'],
})
export class AdminAsociadosComponent implements OnInit {
  @ViewChild('edit') editTemplate: TemplateRef<any>;
  @ViewChild('details') detailsTemplate: TemplateRef<any>;

  modalRef: BsModalRef;

  // Data
  administrators: Administrator[] = [];
  allAdministrators: Administrator[] = []; // Para mantener la lista completa para filtros
  adminDetails: Administrator | null = null;
  selectedAdminId: number | null = null;

  // Estados para el modal de detalles/edición
  isEditMode = false;
  editForm: any = null;
  selectedAlliance: any = null;
  allianceList: any[] = [];

  // Loading states
  loading = false;
  loadingDetails = false;

  // Error states
  error: string | null = null;
  detailsError: string | null = null;

  // Filter variables
  isActiveON = false;
  isInactiveON = false;
  criterioSelector = 'none';
  searchText = '';
  activeFilter: boolean | undefined;
  searchFilter = '';

  // Pagination
  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  itemsPerPage = 10;

  config = {
    animated: true,
    class: 'modal-dialog-centered modal-app modal-app-sm',
    backdrop: true,
    ignoreBackdropClick: true,
  };

  configDetails = {
    animated: true,
    class: 'modal-dialog-centered modal-app modal-app-lg',
    backdrop: true,
    ignoreBackdropClick: true,
  };

  constructor(
    private modalService: BsModalService,
    private adminService: AdminService
  ) { }

  ngOnInit() {
    this.loadAdministrators();
  }

  /**
   * Carga la lista de administradores
   */
  loadAdministrators() {
    this.loading = true;
    this.error = null;

    this.adminService.listAllianceAdministrators().subscribe({
      next: (administrators) => {
        this.allAdministrators = administrators;
        this.totalItems = administrators.length;
        this.applyFilters();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading administrators:', error);
        this.error = 'Error al cargar los administradores';
        this.loading = false;
      }
    });
  }

  /**
   * Aplica los filtros a la lista de administradores
   */
  applyFilters() {
    let filteredAdministrators = this.allAdministrators.slice();

    // Filtro por estado activo/inactivo
    if (this.activeFilter !== undefined) {
      filteredAdministrators = filteredAdministrators.filter(admin => admin.is_active === this.activeFilter);
    }

    // Filtro por búsqueda de texto
    if (this.searchText.trim() && this.criterioSelector !== 'none') {
      const searchTerm = this.searchText.toLowerCase().trim();
      filteredAdministrators = filteredAdministrators.filter(admin => {
        switch (this.criterioSelector) {
          case 'full_name':
            return admin.user.full_name.toLowerCase().includes(searchTerm);
          case 'email':
            return admin.user.email.toLowerCase().includes(searchTerm);
          case 'alliance':
            return admin.alliance.name.toLowerCase().includes(searchTerm);
          case 'laboratory':
            return admin.alliance.laboratory.name.toLowerCase().includes(searchTerm);
          default:
            return true;
        }
      });
      this.searchFilter = this.searchText;
    } else {
      this.searchFilter = '';
    }

    // Actualizar contadores
    this.totalItems = filteredAdministrators.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    this.currentPage = Math.min(this.currentPage, this.totalPages || 1);

    // El pipe paginate maneja el slicing en la plantilla
    this.administrators = filteredAdministrators;
  }

  /**
   * Toggle filtro activos
   */
  onActiveToggle() {
    if (this.isActiveON) {
      this.isActiveON = false;
      this.activeFilter = undefined;
    } else {
      this.isActiveON = true;
      this.isInactiveON = false;
      this.activeFilter = true;
    }
    this.currentPage = 1;
    this.applyFilters();
  }

  /**
   * Toggle filtro inactivos
   */
  onInactiveToggle() {
    if (this.isInactiveON) {
      this.isInactiveON = false;
      this.activeFilter = undefined;
    } else {
      this.isInactiveON = true;
      this.isActiveON = false;
      this.activeFilter = false;
    }
    this.currentPage = 1;
    this.applyFilters();
  }

  /**
   * Cambio de página
   */
  onPageChange(page: number) {
    this.currentPage = page;
    this.applyFilters();
  }

  /**
   * Toggle estado del administrador
   */
  toggleAdminStatus(admin: Administrator) {
    const newStatus = !admin.is_active;

    this.updateAdministrator(
      admin.id,
      admin.user.id,
      admin.alliance.id,
      admin.permission_level,
      newStatus
    ).subscribe({
      next: (updatedAdmin) => {
        // Actualizar en la lista completa
        const allIndex = this.allAdministrators.findIndex(a => a.id === admin.id);
        if (allIndex !== -1) {
          this.allAdministrators[allIndex] = updatedAdmin;
        }

        // Reaplica filtros para actualizar la vista
        this.applyFilters();
      },
      error: (error) => {
        console.error('Error updating administrator status:', error);
        this.error = 'Error al cambiar el estado del administrador';
      }
    });
  }

  /**
   * Crea un nuevo administrador de alianza
   */
  createAllianceAdministrator(
    first_name: string,
    first_name2: string,
    last_name: string,
    last_name2: string,
    email: string,
    cell: string,
    dni: string,
    city_id: string,
    alliance_id: number,
    permission_level: number
  ) {
    return this.adminService.createAllianceAdministrator(
      first_name,
      first_name2,
      last_name,
      last_name2,
      email,
      cell,
      dni,
      city_id,
      alliance_id,
      permission_level
    );
  }

  /**
   * Obtiene un administrador específico por ID
   */
  getAdministrator(id: number) {
    return this.adminService.getAllianceAdministrator(id);
  }

  /**
   * Actualiza un administrador existente
   */
  updateAdministrator(
    id: number,
    user_id: number,
    alliance_id: number,
    permission_level: number,
    is_active: boolean
  ) {
    return this.adminService.updateAllianceAdministrator(
      id,
      user_id,
      alliance_id,
      permission_level,
      is_active
    );
  }

  /**
   * Abre modal directamente en modo edición
   */
  openAdminDetailsModal(administrator: Administrator) {
    this.modalRef = this.modalService.show(this.detailsTemplate, this.configDetails);
    this.selectedAdminId = administrator.id;
    this.loadAdministratorDetails(administrator.id);
    this.loadAlliances(); // Cargar alianzas para el select
  }

  /**
   * Cargar lista de alianzas para el selector
   */
  loadAlliances() {
    this.adminService.listAlliances().subscribe({
      next: (data) => {
        this.allianceList = data.results || data;
      },
      error: (error) => {
        console.log('Error cargando alianzas:', error);
        // Datos mock si no existe el endpoint
        this.allianceList = [
          {
            id: 1,
            name: 'Alianza Cardiovascular',
            laboratory: { id: 1, name: 'Laboratorio Central' }
          },
          {
            id: 2,
            name: 'Alianza Neurológica',
            laboratory: { id: 2, name: 'Laboratorio Especializado' }
          }
        ];
      }
    });
  }

  /**
   * Configurar formulario de edición con datos actuales
   */
  setupEditForm() {
    if (this.adminDetails) {
      this.selectedAlliance = this.adminDetails.alliance;
      this.editForm = {
        // Solo los campos que existen y se pueden editar
        first_name: this.adminDetails.user.first_name,
        last_name: this.adminDetails.user.last_name,
        email: this.adminDetails.user.email,
        // Datos de organización
        alliance_id: this.adminDetails.alliance.id,
        permission_level: this.adminDetails.permission_level,
        is_active: this.adminDetails.is_active
      };
    }
  }

  /**
   * Guardar cambios desde el modal
   */
  /**
  * Guardar cambios desde el modal - VERSIÓN CORREGIDA
  */
  saveChangesFromModal() {
    if (this.adminDetails && this.editForm) {
      this.loadingDetails = true;
      this.detailsError = null;

      // Crear payload completo con TODOS los campos que pide el backend
      const payload: any = {
        // Campos requeridos por el backend
        user_id: this.adminDetails.user.id,
        alliance_id: this.editForm.alliance_id,

        // Datos del usuario
        first_name: this.editForm.first_name,
        last_name: this.editForm.last_name,
        email: this.editForm.email,
        active: this.editForm.is_active,

        // Datos del administrador
        permission_level: this.editForm.permission_level,
        is_active: this.editForm.is_active
      };

      // Solo agregar campos opcionales si tienen datos en el original
      if (this.adminDetails.user.first_name2) {
        payload.first_name2 = this.adminDetails.user.first_name2;
      }
      if (this.adminDetails.user.last_name2) {
        payload.last_name2 = this.adminDetails.user.last_name2;
      }
      if (this.adminDetails.user.cell) {
        payload.cell = this.adminDetails.user.cell;
      }
      if (this.adminDetails.user.dni) {
        payload.dni = this.adminDetails.user.dni;
      }

      console.log('Sending complete payload:', payload);

      // Una sola llamada con todos los datos
      this.updateUserWithCustomPayload(this.adminDetails.id, payload).subscribe({
        next: (response: any) => {
          console.log('Administrator updated successfully', response);

          // Validar que la respuesta tenga la estructura esperada
          if (response && typeof response === 'object' && 'id' in response) {
            const updatedAdmin = response as Administrator;

            // Actualizar detalles del modal
            this.adminDetails = updatedAdmin;

            // Actualizar en la lista completa
            const allIndex = this.allAdministrators.findIndex(a => a.id === updatedAdmin.id);
            if (allIndex !== -1) {
              this.allAdministrators[allIndex] = updatedAdmin;
            }

            // Reaplica filtros para actualizar la vista
            this.applyFilters();

            this.loadingDetails = false;
            // Cerrar modal después de guardar
            this.modalRef.hide();
          } else {
            console.error('Invalid response structure:', response);
            this.detailsError = 'Respuesta inválida del servidor';
            this.loadingDetails = false;
          }
        },
        error: (error) => {
          console.error('Error updating administrator:', error);
          this.detailsError = 'Error al actualizar el administrador';
          this.loadingDetails = false;
        }
      });
    }
  }

  /**
   * Actualizar usuario con payload personalizado usando PATCH - CORREGIDO
   */
  updateUserWithCustomPayload(userId: number, payload: any) {
    const url = `${environment.Api_alliance_administrators_detail_edit}${userId}/`;
    return this.adminService['http'].patch(url, payload); // ← Cambiar PUT por PATCH
  }

  /**
   * Manejar cambio de alianza
   */
  onAllianceChange(event: any) {
    const allianceId = parseInt(event.target.value);
    const alliance = this.allianceList.find(a => a.id === allianceId);
    if (alliance) {
      this.selectedAlliance = alliance;
      this.editForm.alliance_id = allianceId;
    }
  }

  /**
   * Carga los detalles completos del administrador y configura el formulario
   */
  loadAdministratorDetails(adminId: number) {
    this.loadingDetails = true;
    this.detailsError = null;
    this.adminDetails = null;

    this.getAdministrator(adminId).subscribe({
      next: (adminDetails) => {
        this.adminDetails = adminDetails;
        this.setupEditForm(); // Configurar formulario directamente
        this.loadingDetails = false;
      },
      error: (error) => {
        console.error('Error loading administrator details:', error);
        this.detailsError = 'Error al cargar los detalles del administrador';
        this.loadingDetails = false;
      }
    });
  }

  /**
   * Edita desde el modal de detalles
   */
  editFromDetails() {
    if (this.adminDetails && this.editTemplate) {
      // Cerrar el modal de detalles
      this.modalRef.hide();

      // Pequeña pausa para que se cierre el modal anterior, luego abrir modal de edición
      setTimeout(() => {
        this.openModalEditAssociate(this.editTemplate, this.adminDetails);
      }, 300);
    }
  }

  /**
   * Cambia el estado desde el modal de detalles
   */
  toggleStatusFromDetails() {
    if (this.adminDetails) {
      const newStatus = !this.adminDetails.is_active;

      this.updateAdministrator(
        this.adminDetails.id,
        this.adminDetails.user.id,
        this.adminDetails.alliance.id,
        this.adminDetails.permission_level,
        newStatus
      ).subscribe({
        next: (updatedAdmin) => {
          // Actualizar detalles del modal
          this.adminDetails = updatedAdmin;

          // Actualizar en la lista completa
          const allIndex = this.allAdministrators.findIndex(a => a.id === updatedAdmin.id);
          if (allIndex !== -1) {
            this.allAdministrators[allIndex] = updatedAdmin;
          }

          // Reaplica filtros para actualizar la vista
          this.applyFilters();
        },
        error: (error) => {
          console.error('Error updating administrator status from details:', error);
          this.detailsError = 'Error al cambiar el estado del administrador';
        }
      });
    }
  }

  /**
   * Maneja la creación de un nuevo administrador
   */
  onCreateAdministrator(administratorData: CreateAdministratorRequest) {
    this.loading = true;
    this.error = null;

    this.createAllianceAdministrator(
      administratorData.first_name,
      administratorData.first_name2 || '',
      administratorData.last_name,
      administratorData.last_name2 || '',
      administratorData.email,
      administratorData.cell,
      administratorData.dni,
      administratorData.city_id,
      administratorData.alliance_id,
      administratorData.permission_level
    ).subscribe({
      next: (newAdministrator) => {
        console.log('Administrator created successfully:', newAdministrator);
        this.allAdministrators.push(newAdministrator);
        this.applyFilters();
        this.loading = false;
        this.modalRef.hide();
      },
      error: (error) => {
        console.error('Error creating administrator:', error);
        this.error = 'Error al crear el administrador';
        this.loading = false;
      }
    });
  }

  /**
   * Maneja la actualización de un administrador
   */
  onUpdateAdministrator(
    id: number,
    user_id: number,
    alliance_id: number,
    permission_level: number,
    is_active: boolean
  ) {
    this.loading = true;
    this.error = null;

    this.updateAdministrator(id, user_id, alliance_id, permission_level, is_active).subscribe({
      next: (updatedAdministrator) => {
        console.log('Administrator updated successfully:', updatedAdministrator);

        // Actualizar la lista completa
        const allIndex = this.allAdministrators.findIndex(admin => admin.id === id);
        if (allIndex !== -1) {
          this.allAdministrators[allIndex] = updatedAdministrator;
        }

        this.applyFilters();
        this.loading = false;
        this.modalRef.hide();
      },
      error: (error) => {
        console.error('Error updating administrator:', error);
        this.error = 'Error al actualizar el administrador';
        this.loading = false;
      }
    });
  }

  /**
   * Abre el modal para crear un nuevo administrador de alianza
   */
  openModalCreateAssociate(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template, this.config);
  }

  /**
   * Abre el modal para editar un administrador existente
   */
  openModalEditAssociate(template: TemplateRef<any>, administrator: Administrator) {
    this.modalRef = this.modalService.show(template, this.config);
    if (this.modalRef.content) {
      this.modalRef.content.administrator = administrator;
    }
  }

  /**
   * Obtiene el texto del nivel de permisos
   */
  getPermissionLevelText(level: number): string {
    const levels: { [key: number]: string } = {
      1: 'Básico',
      2: 'Intermedio',
      3: 'Avanzado',
      4: 'Administrador',
      5: 'Super Administrador'
    };
    return levels[level] || 'Desconocido';
  }

  /**
   * Obtiene la clase CSS para el estado activo/inactivo
   */
  getStatusClass(isActive: boolean): string {
    return isActive ? 'badge-success' : 'badge-secondary';
  }

  /**
   * Obtiene el texto del estado
   */
  getStatusText(isActive: boolean): string {
    return isActive ? 'Activo' : 'Inactivo';
  }

  /**
   * TrackBy function para optimizar la renderización
   */
  trackByAdminId(index: number, admin: Administrator): number {
    return admin.id;
  }
}
